import { CaretDownIcon } from '#icons';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  ButtonProps,
  Stack,
  Theme,
  Typography,
} from '@mui/material';
import React, { CSSProperties, FC, useEffect, useMemo, useState } from 'react';

interface StyleProps {
  isExpanded: boolean;
  hasSubTitle: boolean;
  isExpansionLocked: boolean;
  accordionDetailsPadding?: CSSProperties['padding'];
}

const useSxProps = (props: StyleProps) => {
  return useMemo(() => {
    return {
      accordionRoot: {
        overflow: 'hidden',
        backgroundColor: (theme: Theme) => theme.palette.background.default,
        border: (theme: Theme) => `1px solid ${theme.palette.borderState?.default ?? theme.palette.divider}`,
        borderRadius: (theme: Theme) => `${theme.spacing(1.5)} !important`,
        boxShadow: 'none',
        '&.MuiAccordion-root:before': {
          backgroundColor: 'transparent',
        },
      },
      accordionSummaryRoot: (() => {
        const padding = props.hasSubTitle ? '12px 20px' : '8px 20px';
        const base = {
          backgroundColor: (theme: Theme) => theme.palette.background.contrast,
          borderTopLeftRadius: (theme: Theme) => theme.spacing(1.5),
          borderTopRightRadius: (theme: Theme) => theme.spacing(1.5),
          minHeight: '48px !important',
          padding,
          '& .MuiAccordionSummary-content': {
            margin: 'inherit !important',
            width: '100%',
            minWidth: 0,
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            padding: '6px',
          },
          cursor: props.isExpansionLocked ? 'default !important' : 'pointer',
        };

        if (props.isExpanded) {
          return {
            ...base,
            borderBottom: (theme: Theme) => `1px solid ${theme.palette.borderState?.default ?? theme.palette.divider}`,
          };
        }

        return {
          ...base,
          borderBottomLeftRadius: (theme: Theme) => theme.spacing(1.5),
          borderBottomRightRadius: (theme: Theme) => theme.spacing(1.5),
        };
      })(),
      accordionDetailsRoot: {
        display: 'block',
        padding: (theme: Theme) => props.accordionDetailsPadding ?? theme.spacing(2.5),
      },
    };
  }, [props]);
};

export interface AccordionCardProps {
  title: string | JSX.Element;

  subTitle?: string;

  isExpanded?: boolean;

  defaultExpanded?: boolean;

  isExpansionLocked?: boolean;

  showExpandIcon?: boolean;

  accordionDetailsPadding?: CSSProperties['padding'];

  onClick?: (isExpanded: boolean) => void;

  showActionButton?: boolean;

  actionButtonProps?: ButtonProps;

  actionButtonText?: string;

  children?: React.ReactNode;
}

export const AccordionCard: FC<AccordionCardProps> = ({
  title,
  subTitle,
  isExpanded,
  defaultExpanded = true,
  isExpansionLocked,
  showExpandIcon = true,
  accordionDetailsPadding,
  showActionButton = false,
  actionButtonProps,
  actionButtonText,
  onClick,
  children,
}) => {
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const sxProps = useSxProps({
    isExpanded: expanded,
    hasSubTitle: !!subTitle,
    isExpansionLocked: !!isExpansionLocked,
    accordionDetailsPadding,
  });

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    if (isExpanded !== undefined) {
      setExpanded(isExpanded);
    }
  }, [isExpanded]);

  const onActionButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();

    if (actionButtonProps?.onClick) {
      actionButtonProps.onClick(e);
    }
  };

  return (
    <Accordion
      // eslint-disable-next-line no-restricted-syntax
      sx={sxProps.accordionRoot}
      expanded={expanded}
      onChange={() => {
        if (isExpansionLocked) {
          return;
        }

        if (onClick) {
          onClick(!expanded);
        }
        if (isExpanded === undefined) {
          setExpanded(!expanded);
        }
      }}
      disableGutters
    >
      <AccordionSummary
        expandIcon={showExpandIcon ? <CaretDownIcon /> : undefined}
        // eslint-disable-next-line no-restricted-syntax
        sx={sxProps.accordionSummaryRoot}
      >
        <Stack direction="row" justifyContent="space-between" width="100%" alignItems="center">
          <Stack minWidth={0}>
            {React.isValidElement(title) ? (
              title
            ) : (
              <Typography variant="body1" fontWeight={600}>
                {title}
              </Typography>
            )}
            {subTitle && (
              <Typography color="text.secondary" variant="body2">
                {subTitle}
              </Typography>
            )}
          </Stack>
          {showActionButton ? (
            <Button variant="contained" {...actionButtonProps} onClick={onActionButtonClick}>
              {actionButtonText}
            </Button>
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        // eslint-disable-next-line no-restricted-syntax
        sx={sxProps.accordionDetailsRoot}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

export default AccordionCard;
